const RESERVED = new Set(["admin", "system", "operator", "owner", "team"]);

export const LEGACY_REVIEWER_ID_KEY = "vmc.reviewerId";

export function normalizeDisplayName(raw: string): string {
  return raw.trim().toLowerCase().replace(/\s+/g, " ");
}

export type DisplayNameErrorCode =
  | "empty"
  | "too_short"
  | "too_long"
  | "bad_chars"
  | "reserved";

export interface DisplayNameError {
  code: DisplayNameErrorCode;
  message: string;
}

export function validateDisplayName(
  raw: string,
  teamSlug: string,
): DisplayNameError | null {
  const n = normalizeDisplayName(raw);
  if (n.length === 0) return { code: "empty", message: "Please enter your name." };
  if (n.length < 2) return { code: "too_short", message: "Name must be at least 2 characters." };
  if (n.length > 40) return { code: "too_long", message: "Name must be 40 characters or fewer." };
  if (!/^[a-z0-9 _.\-']+$/.test(n)) {
    return { code: "bad_chars", message: "Letters, digits, spaces, and . _ - ' only." };
  }
  // The admin team (slug='admin') is exempt from the reserved-name and
  // slug-match checks — it's the one account that legitimately needs to
  // identify itself as "admin". Worker enforces the slug↔role invariant,
  // so this exemption can't be exploited by other teams.
  if (teamSlug.toLowerCase() !== "admin") {
    if (RESERVED.has(n) || n === teamSlug.toLowerCase()) {
      return { code: "reserved", message: "That name is reserved." };
    }
  }
  return null;
}

export async function deriveReviewerId(
  teamSlug: string,
  displayName: string,
): Promise<string> {
  const norm = normalizeDisplayName(displayName);
  const bytes = new TextEncoder().encode(`${teamSlug.toLowerCase()}:${norm}`);
  const digest = await crypto.subtle.digest("SHA-256", bytes);
  return [...new Uint8Array(digest)]
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

export function clearLegacyReviewerId(): void {
  try {
    localStorage.removeItem(LEGACY_REVIEWER_ID_KEY);
  } catch {
    // SSR / test environments without localStorage; no-op.
  }
}
